/**
 * Billing service — calls the webadmin2 HTTP API (API Gateway + Lambda).
 * Base URL comes from the CloudFormation output `ApiEndpoint`.
 */
const API_BASE =
  (import.meta.env.VITE_API_BASE as string | undefined) ??
  "http://localhost:3000";

export interface BillingItem {
  period:         string;
  country:        string;
  cuit:           string;
  collector:      string;
  collector_long: string;
  acquirer:       string;
  psp:            string;
  processed:      number;
  count:          number;
  billing_amount: number;
  // `null`/ausente = el proceso manual upstream dejó de escribir PENDING; se
  // trata como PENDIENTE (facturable). REJECTED/TIMEOUT = resultado real de AFIP.
  status:         "PENDING" | "PREPARED" | "EMITTING" | "SENT" | "APPROVED" | "PARTIAL" | "REJECTED" | "TIMEOUT" | null;
  invoice_number: string | null;
  cae:            string | null;
  cae_vto:        string | null;
  invoiced_at:    string | null;   // ISO-8601 — fecha real de emisión
  cbte_fch?:      string | null;   // yyyyMMdd — fecha del comprobante en AFIP (la que lleva la factura)
  datetime?:      string | null;   // ISO-8601 — fecha de la última corrida del proceso manual
  emitter_cuit:   number | null;   // null para rows PENDING (no asignado todavía)
  // Empresa dueña de la row pendiente ("Transferencias SAS" → solo facturable
  // por Transferencias; otro valor o ausente → solo B2BBNPL). Lo determina LA BD
  // (collection_config del producto) — nunca el usuario (CP-125).
  billing_company?: string | null;
  /** true = la empresa se cambió a mano desde la UI; `refresh-companies` la respeta. */
  billing_company_manual?: boolean;
  // true = factura creada a mano desde el diálogo (no vino del archivo upstream).
  manual?:          boolean;
  // Monto congelado al emitir (fuente de verdad de lo facturado). Si difiere de
  // billing_amount, el proceso manual pisó el monto después de facturar → "*".
  invoiced_amount?: number | null;
  invoiced_net?:    number | null;
  invoiced_tax?:    number | null;
  afip_message?:    string | null; // motivo de rechazo/timeout de AFIP
  // Only populated when the listing is grouped by CUIT (distinctCuit=true).
  pending_amount?:  number;
  pending_count?:   number;
  approved_count?:  number;
  /** Collectors todavía no emitidos del grupo (los que recibirían la factura
   *  agrupada y entre los que se reparte una edición de monto). */
  pending_collectors?: string[];
}

export interface BillingList {
  period:        string;
  period_start:  string;
  period_end:    string;
  items_count:   number;
  total_billing: number;
  items:         BillingItem[];
}

function authHeader(): Record<string, string> {
  const jwt = localStorage.getItem("webadmin2.jwt");
  return jwt ? { Authorization: `Bearer ${jwt}` } : {};
}

/**
 * Small fetch wrapper that redirects to /login on 401. The JWT issued by
 * CucuruCoreDBApi expires in ~59 min; without this, the UI keeps the stale
 * token and every request fails with 401 until the user logs out manually.
 */
async function authFetch(url: string, init: RequestInit = {}): Promise<Response> {
  const res = await fetch(url, init);
  if (res.status === 401) {
    localStorage.removeItem("webadmin2.jwt");
    localStorage.removeItem("webadmin2.email");
    if (typeof window !== "undefined") {
      window.location.replace("/login");
    }
  }
  return res;
}

export interface BillingFilters {
  from?:          string;
  to?:            string;
  period?:        string;    // shorthand 'YYYYMMDD-YYYYMMDD' for exact PK query
  status?:        string;
  minAmount?:     number;    // only rows with billing_amount >= minAmount
  distinctCuit?:  boolean;   // collapse rows with same CUIT
  emitterCuit?:   number;    // filtra APPROVED por emitter_cuit y PENDING por billing_company
}

export async function listBilling(f: BillingFilters = {}): Promise<BillingList> {
  const qs = new URLSearchParams();
  if (f.from)                            qs.set("from",          f.from);
  if (f.to)                              qs.set("to",            f.to);
  if (f.period)                          qs.set("period",        f.period);
  if (f.status)                          qs.set("status",        f.status);
  if (f.minAmount && f.minAmount > 0)    qs.set("min_amount",    String(f.minAmount));
  if (f.distinctCuit)                    qs.set("distinct_cuit", "true");
  if (f.emitterCuit !== undefined)       qs.set("emitter_cuit",  String(f.emitterCuit));
  const res = await authFetch(
    `${API_BASE}/billing/items${qs.toString() ? `?${qs}` : ""}`,
    { headers: { ...authHeader() } },
  );
  if (!res.ok) throw new Error(`GET /billing/items → ${res.status}`);
  return res.json();
}

export interface PrepareResultItem {
  id:           string;
  status:       "PREPARED";
  number:       number;
  ptovta:       number;
  cbte_tipo:    number;
  invoice:      string;             // "00002-00000487"
  emitter_cuit: number;
  preview_text: string;
}

export interface PrepareResultError {
  id:     string;
  error:  string;
  number?: number;        // si error="already_prepared", el número que ya tiene
  cae?:    string;        // si error="already_approved"
  detail?: string;
  missing?: string[];     // si error="receptor_incomplete"
  wasted_number?: number; // si error="race_condition"
}

export interface PrepareResult {
  prepared:     number;
  failed:       number;
  emitter_cuit: number;
  items:        PrepareResultItem[];
  errors:       PrepareResultError[];
}

/**
 * Reserva CbteNros y marca rows PENDING → PREPARED.
 *
 * Cada id es del formato `daterange|collector` (mismo que send-to-arca).
 * `emitterCuit` opcional — default = B2BBNPL S.A. para back-compat.
 */
export async function prepareInvoices(
  ids: string[],
  emitterCuit?: number,
): Promise<PrepareResult> {
  const res = await authFetch(`${API_BASE}/billing/prepare`, {
    method:  "POST",
    headers: { "Content-Type": "application/json", ...authHeader() },
    body:    JSON.stringify({ ids, emitter_cuit: emitterCuit }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body?.error ?? `POST /billing/prepare → ${res.status}`);
  }
  return res.json();
}

export interface LastAuthorizedResponse {
  emitter_cuit: number;
  ptovta:       number;
  cbte_tipo:    number;
  last:         number;
  next:         number;
  environment:  "PROD" | "DEV";
}

/**
 * Wrapper de `FECompUltimoAutorizado` (AFIP). Útil para mostrar al operador
 * "el próximo número va a ser X" antes de un Prepare/Send.
 */
export async function lastAuthorized(
  emitterCuit?: number,
  ptovta?: number,
  cbteTipo: number = 1,
): Promise<LastAuthorizedResponse> {
  const qs = new URLSearchParams();
  if (emitterCuit !== undefined) qs.set("emitter_cuit", String(emitterCuit));
  if (ptovta !== undefined)      qs.set("ptovta",       String(ptovta));
  qs.set("cbte_tipo", String(cbteTipo));
  const res = await authFetch(
    `${API_BASE}/billing/arca/last-authorized?${qs}`,
    { headers: { ...authHeader() } },
  );
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body?.error ?? `GET last-authorized → ${res.status}`);
  }
  return res.json();
}

/**
 * Re-sincroniza el counter local con AFIP. Para usar después de huecos en la
 * numeración (PREPARED nunca emitidos) o si ops emitió por portal AFIP fuera
 * de este sistema.
 */
export async function resyncCounter(
  emitterCuit?: number,
  ptovta?: number,
  cbteTipo: number = 1,
): Promise<{ counter_key: string; afip_last: number; next_number: number }> {
  const res = await authFetch(`${API_BASE}/billing/arca/resync-counter`, {
    method:  "POST",
    headers: { "Content-Type": "application/json", ...authHeader() },
    body:    JSON.stringify({
      emitter_cuit: emitterCuit,
      ptovta,
      cbte_tipo:    cbteTipo,
    }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body?.error ?? `POST resync-counter → ${res.status}`);
  }
  return res.json();
}

export interface SendResultItem {
  id:        string;
  ok:        boolean;
  error?:    string;
  cae?:      string;
  cae_vto?:  string;
  cbtenro?:  number;
  ptovta?:   number;
  cbte_tipo?: number;
}

export interface SendResult {
  requested: number;
  approved:  number;
  results:   SendResultItem[];
}

export async function sendToArca(
  ids: string[],
  grouped: boolean = false,
  emitterCuit?: number,
  /** Fecha del comprobante elegida a mano (yyyyMMdd). El backend la rechaza con
   *  409 si el período todavía no la tiene destrabada — ver getCbteFecha. */
  cbteFch?: string,
  /** Huellas de la previsualización ({id: fingerprint}, ver previewArca). El
   *  backend no emite el comprobante cuya huella ya no coincide. */
  expected?: Record<string, string>,
): Promise<SendResult> {
  const res = await authFetch(`${API_BASE}/billing/send-to-arca`, {
    method:  "POST",
    headers: { "Content-Type": "application/json", ...authHeader() },
    body:    JSON.stringify({ ids, grouped, emitter_cuit: emitterCuit, cbte_fch: cbteFch, expected }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body?.error ?? `POST /billing/send-to-arca → ${res.status}`);
  }
  return res.json();
}

/**
 * Previsualización de la emisión (CP-588): qué va a salir a AFIP por cada id,
 * armado por el servidor con las mismas funciones que la emisión y sin
 * reservar números ni escribir nada. `afip` son los campos de FECAESolicitar
 * tal cual se mandan; `fingerprint` es la huella que después viaja en
 * `expected` a sendToArca.
 */
export interface ArcaPreviewRow {
  daterange:        string;
  collector:        string;
  status:           string | null;
  amount:           number;
  net_amount:       number;
  tax_amount:       number;
  /** total que escribió el archivo del período (invoice_text); null si no se pudo leer */
  upstream_total:   number | null;
  amount_before:    number | null;
  amount_edited_at: string | null;
  amount_edited_by: string | null;
  edited:           boolean;
  manual:           boolean;
  billing_company:  string | null;
  number:           number | null;
}

export interface ArcaPreviewInvoice {
  id:             string;
  ok:             boolean;
  errors:         string[];
  warnings:       string[];
  emitter_cuit:   number;
  emitter:        string;
  receptor:       { cuit: string; razon_social: string; condicion_iva_id: number; condicion_iva_desc: string; email: string };
  cbte_tipo:      number;
  cbte_tipo_desc: string;
  ptovta:         number;
  cbtenro:        number | null;
  number_source:  "reservado" | "estimado" | "desconocido";
  fecha:          string;
  period:         string;
  imp_total:      number;
  imp_neto:       number;
  imp_iva:        number;
  rows:           ArcaPreviewRow[];
  afip:           Record<string, unknown> | null;
  fingerprint:    string;
  grouped_cuit?:  string;
}

export interface ArcaPreview {
  generated_at:    string;
  grouped:         boolean;
  emitter_cuit:    number;
  emitter:         string;
  afip_verified:   boolean;
  afip_note:       string | null;
  cbte_fch_manual: string | null;
  count:           number;
  ok_count:        number;
  total:           number;
  total_neto:      number;
  total_iva:       number;
  invoices:        ArcaPreviewInvoice[];
  expected:        Record<string, string>;
}

export async function previewArca(
  ids: string[], grouped: boolean, emitterCuit?: number, cbteFch?: string,
): Promise<ArcaPreview> {
  const res = await authFetch(`${API_BASE}/billing/arca/preview`, {
    method:  "POST",
    headers: { "Content-Type": "application/json", ...authHeader() },
    body:    JSON.stringify({ ids, grouped, emitter_cuit: emitterCuit, cbte_fch: cbteFch }),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body?.error ?? `POST /billing/arca/preview → ${res.status}`);
  return body;
}

export interface Emitter {
  cuit:         number;
  razon_social: string;
  ptovta:       number;
}

export interface EmittersResponse {
  default_cuit: number;
  emitters:     Emitter[];
}

/** Cambia a mano la empresa que factura una row del período. Solo rows NO
 *  emitidas: una vez que hay CAE, el emisor quedó fijo en AFIP (el backend
 *  devuelve 409). Marca la row para que `refreshBillingCompanies` no la pise. */
export async function updateBillingCompany(
  period: string, collector: string, emitterCuit: number,
): Promise<{ billing_company: string; billing_company_manual: boolean }> {
  const res = await authFetch(
    `${API_BASE}/billing/items/${period}/${encodeURIComponent(collector)}/company`,
    {
      method:  "PUT",
      headers: { "Content-Type": "application/json", ...authHeader() },
      body:    JSON.stringify({ emitter_cuit: emitterCuit }),
    });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body?.error ?? `PUT company → ${res.status}`);
  return body;
}

/**
 * Fecha que va a llevar el comprobante del período, y si se puede editar.
 *
 * `editable` es false mientras el batch del período esté en curso: dentro de los
 * `retro_max_days` (hasta el día 10 del mes siguiente) rige la emisión
 * retroactiva al último día del mes facturado, y además no puede quedar ninguna
 * factura del período sin emitir. Recién con ambas resueltas se habilita.
 */
export interface CbteFechaStatus {
  period:         string;
  period_end:     string;          // yyyyMMdd — último día del mes facturado
  today:          string;          // yyyyMMdd en hora Argentina
  fecha:          string;          // yyyyMMdd que se usaría hoy
  verified:       boolean;         // false = no se pudo chequear contra AFIP
  retroactiva:    boolean;
  days_late:      number | null;
  retro_max_days: number;
  pending:        number;          // facturas del período sin emitir
  editable:       boolean;
  reason:         string;          // por qué NO es editable ("" si lo es)
  emitter_cuit:   number;
}

export async function getCbteFecha(period: string, emitterCuit?: number): Promise<CbteFechaStatus> {
  const qs = new URLSearchParams({ period });
  if (emitterCuit !== undefined) qs.set("emitter_cuit", String(emitterCuit));
  const res = await authFetch(`${API_BASE}/billing/cbte-fecha?${qs}`, { headers: { ...authHeader() } });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body?.error ?? `GET /billing/cbte-fecha → ${res.status}`);
  return body;
}

/** yyyyMMdd → dd/MM/yyyy. Sin timezone: es una fecha fiscal, no un instante. */
export function fmtCbteFecha(yyyymmdd: string): string {
  if (!/^\d{8}$/.test(yyyymmdd)) return yyyymmdd || "—";
  return `${yyyymmdd.slice(6, 8)}/${yyyymmdd.slice(4, 6)}/${yyyymmdd.slice(0, 4)}`;
}

export async function listEmitters(): Promise<EmittersResponse> {
  const res = await authFetch(`${API_BASE}/billing/emitters`, {
    headers: { ...authHeader() },
  });
  if (!res.ok) throw new Error(`GET /billing/emitters → ${res.status}`);
  return res.json();
}

export async function downloadInvoicesZip(ids: string[]): Promise<string> {
  const res = await authFetch(`${API_BASE}/billing/items/zip`, {
    method:  "POST",
    headers: { "Content-Type": "application/json", ...authHeader() },
    body:    JSON.stringify({ ids }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body?.error ?? `POST /billing/items/zip → ${res.status}`);
  }
  const blob = await res.blob();
  return URL.createObjectURL(blob);
}

// Sin opts: envío normal al receptor (idempotente por mailed_at). Con
// opts.copyTo (CP-157): manda una COPIA del PDF de una factura ya emitida SOLO
// a ese email y, si saveToReceptor (default true), lo agrega al receptor para
// las próximas facturas.
export async function sendInvoiceEmail(
  daterange: string, collector: string,
  opts?: { copyTo: string; saveToReceptor?: boolean },
): Promise<{ok: boolean; message_id?: string; recipient?: string; subject?: string;
            receptor_updated?: boolean | string}> {
  const res = await authFetch(
    `${API_BASE}/billing/items/${encodeURIComponent(daterange)}/${encodeURIComponent(collector)}/email`,
    opts?.copyTo
      ? { method: "POST", headers: { ...authHeader(), "Content-Type": "application/json" },
          body: JSON.stringify({ copy_to: opts.copyTo, save_to_receptor: opts.saveToReceptor ?? true }) }
      : { method: "POST", headers: { ...authHeader() } },
  );
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body?.detail ?? body?.error ?? `POST email → ${res.status}`);
  }
  return res.json();
}

/**
 * Fetches the generated PDF for an APPROVED invoice and returns a Blob URL
 * the caller can open in a new tab or use as an <a href> target. The caller
 * is responsible for `URL.revokeObjectURL(url)` when done (or just let the
 * tab lifecycle handle it).
 */
export async function getInvoicePdfUrl(daterange: string, collector: string): Promise<string> {
  const res = await authFetch(
    `${API_BASE}/billing/items/${encodeURIComponent(daterange)}/${encodeURIComponent(collector)}/pdf`,
    { headers: { ...authHeader() } },
  );
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body?.error ?? `GET pdf → ${res.status}`);
  }
  const blob = await res.blob();
  return URL.createObjectURL(blob);
}

/** Crea una factura manual (cliente/monto que no vienen en el archivo). 409 si
 *  ya existe la fila O si la facturación estándar del período aún no se emitió
 *  completa (CP-125: las manuales van después del batch). La empresa emisora
 *  (billing_company) la determina la BD, no el caller.
 *  `emitter_cuit` = pestaña activa: define `billing_company` y por lo tanto qué empresa puede facturarla. */
export async function createBillingItem(input: { period: string; collector: string; cuit: string; amount: number }): Promise<BillingItem> {
  const res = await authFetch(`${API_BASE}/billing/items`, {
    method:  "POST",
    headers: { "Content-Type": "application/json", ...authHeader() },
    body:    JSON.stringify(input),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body?.error ?? `POST /billing/items → ${res.status}`);
  return body;
}

/** Re-estampa billing_company de las rows NO facturadas del período desde
 *  collection_config (la BD manda). Usar cuando el atributo cambió upstream. */
export async function refreshBillingCompanies(period: string): Promise<{ checked: number; updated: number; changes: Array<{collector: string; from: string | null; to: string}> }> {
  const res = await authFetch(`${API_BASE}/billing/refresh-companies`, {
    method:  "POST",
    headers: { "Content-Type": "application/json", ...authHeader() },
    body:    JSON.stringify({ period }),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body?.error ?? `POST /billing/refresh-companies → ${res.status}`);
  return body;
}

export async function updateBillingAmount(daterange: string, collector: string, amount: number): Promise<{amount:number,net_amount:number,tax_amount:number}> {
  const res = await authFetch(
    `${API_BASE}/billing/items/${encodeURIComponent(daterange)}/${encodeURIComponent(collector)}`,
    {
      method:  "PUT",
      headers: { "Content-Type": "application/json", ...authHeader() },
      body:    JSON.stringify({ amount }),
    },
  );
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body?.detail ?? body?.error ?? `PUT /billing/items → ${res.status}`);
  }
  return res.json();
}

export interface CuitAmountUpdate {
  daterange:  string;
  cuit:       string;
  amount:     number;
  net_amount: number;
  tax_amount: number;
  rows:       Array<{ collector: string; before: number; amount: number; net_amount: number; tax_amount: number }>;
  excluded:   { already_invoiced: string[]; other_emitter: string[] };
}

/** Modo agrupado: edita el TOTAL pendiente de un CUIT. El backend lo reparte
 *  entre las filas pendientes del grupo (las mismas que entrarían a la factura
 *  agrupada) en proporción a sus montos actuales; antes se escribía en una
 *  sola fila y la factura salía por otro monto. */
export async function updateBillingCuitAmount(
  daterange: string, cuit: string, amount: number, emitterCuit?: number,
): Promise<CuitAmountUpdate> {
  const res = await authFetch(
    `${API_BASE}/billing/items/${encodeURIComponent(daterange)}/cuit/${encodeURIComponent(cuit)}`,
    {
      method:  "PUT",
      headers: { "Content-Type": "application/json", ...authHeader() },
      body:    JSON.stringify({ amount, emitter_cuit: emitterCuit ?? null }),
    },
  );
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body?.detail ?? body?.error ?? `PUT /billing/items/cuit → ${res.status}`);
  }
  return res.json();
}

// ----- Receptor config -----
export interface Receptor {
  cuit:               string;
  razon_social:       string;
  condicion_iva_id:   number | null;
  condicion_iva_desc: string;
  actividad:          number | null;
  email:              string;
  domicilio:          string;
  /** Jurisdicción del domicilio (nombre canónico de PROVINCIAS_AR). Fuente
   *  prioritaria del informe de facturación por provincia (IIBB, CP-250). */
  provincia:          string;
  source:             "excel" | "manual" | "arca" | "";
  updated_at:         string;
}

/** Nombres canónicos que acepta el backend (provincias.py). El PUT también
 *  admite alias/códigos, pero el selector usa estos tal cual. */
export const PROVINCIAS_AR: string[] = [
  "Ciudad Autónoma de Buenos Aires", "Buenos Aires", "Catamarca", "Chaco", "Chubut",
  "Córdoba", "Corrientes", "Entre Ríos", "Formosa", "Jujuy", "La Pampa", "La Rioja",
  "Mendoza", "Misiones", "Neuquén", "Río Negro", "Salta", "San Juan", "San Luis",
  "Santa Cruz", "Santa Fe", "Santiago del Estero", "Tierra del Fuego", "Tucumán",
];

export async function listReceptors(): Promise<Record<string, Receptor>> {
  const res = await authFetch(`${API_BASE}/billing/receptors`, { headers: { ...authHeader() } });
  if (!res.ok) throw new Error(`GET /billing/receptors → ${res.status}`);
  const d: { items: Receptor[] } = await res.json();
  return Object.fromEntries(d.items.map((r) => [r.cuit, r]));
}

export async function saveReceptor(cuit: string, data: Partial<Receptor>): Promise<Receptor> {
  const res = await authFetch(`${API_BASE}/billing/receptors/${encodeURIComponent(cuit)}`, {
    method:  "PUT",
    headers: { "Content-Type": "application/json", ...authHeader() },
    body:    JSON.stringify(data),
  });
  if (!res.ok) throw new Error(`PUT /billing/receptors/${cuit} → ${res.status}`);
  return res.json();
}

/** Lo que devuelve el padrón de ARCA (ws_sr_constancia_inscripcion). Es un
 *  subconjunto de Receptor: no trae `actividad`, `email` ni `updated_at`, y la
 *  condición/domicilio son best-effort (el padrón no siempre los expone). */
export interface ArcaLookup {
  cuit:                string;
  razon_social:        string;
  condicion_iva_id?:   number;
  condicion_iva_desc?: string;
  domicilio?:          string;
  /** Provincia del domicilio fiscal (idProvincia / descripcionProvincia / CP del padrón), ya normalizada. */
  provincia?:          string;
  provincia_id?:       number;
  /** Bloque crudo del domicilio fiscal de ARCA (CP-374). El backend lo persiste
   *  en webadmin2-arca-padron con la fecha de consulta. */
  domicilio_fiscal?: {
    direccion: string; localidad: string; cod_postal: string;
    id_provincia: number | null; descripcion_provincia: string;
  };
  source?:             string;
}

export async function lookupArca(cuit: string): Promise<ArcaLookup> {
  const res = await authFetch(`${API_BASE}/billing/receptors/${encodeURIComponent(cuit)}/arca-lookup`, {
    method:  "POST",
    headers: { "Content-Type": "application/json", ...authHeader() },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body?.error ?? body?.detail ?? `arca-lookup failed (${res.status})`);
  }
  return res.json();
}

// Catálogo AFIP FEParamGetCondicionIvaReceptor (dejo hardcoded para el dropdown)
export const CONDICION_IVA_OPTIONS: Array<{ id: number; desc: string; for_cmp: string }> = [
  { id: 1,  desc: "IVA Responsable Inscripto",                          for_cmp: "A / C" },
  { id: 6,  desc: "Responsable Monotributo",                            for_cmp: "A / C" },
  { id: 13, desc: "Monotributista Social",                              for_cmp: "A / C" },
  { id: 16, desc: "Monotributo Trabajador Independiente Promovido",     for_cmp: "A / C" },
  { id: 4,  desc: "IVA Sujeto Exento",                                  for_cmp: "B / C" },
  { id: 7,  desc: "Sujeto No Categorizado",                             for_cmp: "B / C" },
  { id: 8,  desc: "Proveedor del Exterior",                             for_cmp: "B / C" },
  { id: 9,  desc: "Cliente del Exterior",                               for_cmp: "B / C" },
  { id: 10, desc: "IVA Liberado – Ley N° 19.640",                       for_cmp: "B / C" },
  { id: 15, desc: "IVA No Alcanzado",                                   for_cmp: "B / C" },
  { id: 5,  desc: "Consumidor Final",                                   for_cmp: "C" },
];

// Códigos de actividad AFIP usadas por Cucuru (B2BBNPL S.A.)
export const ACTIVIDAD_OPTIONS: Array<{ id: number; desc: string }> = [
  { id: 829100, desc: "Servicios de agencias de cobro y calificación crediticia" },
  { id: 620900, desc: "Servicios de informática n.c.p." },
];

// ---------- Facturación por procesador (+ cuenta) ----------
export interface BillingProcAccount {
  account:     string;
  fact_count:  number;
  fact_monto:  number;
  pend_count:  number;
  pend_monto:  number;
}
export interface BillingProcessor {
  processor: string;
  accounts:  BillingProcAccount[];
  totals:    { fact_count: number; fact_monto: number; pend_count: number; pend_monto: number };
}
export interface BillingByProcessor {
  period:       string;
  processors:   BillingProcessor[];
  generated_at: string;
}

// ----- CP-250: informe de facturación por provincia (IIBB) -----
export type InformeCriterio = "comprobante" | "periodo";

export interface InformeProvinciaRow {
  provincia:    string;          // "Sin provincia" para lo no identificable
  provincia_id: number | null;   // id de jurisdicción AFIP
  neto:         number;
  iva:          number;
  total:        number;
  pct_neto:     number;          // % sobre el neto del mes
  pct_total:    number;          // % sobre el total (con IVA) del mes
  facturas:     number;          // CAEs distintos (la emisión agrupada comparte CAE)
  clientes:     number;          // CUITs distintos
  filas:        number;
}

export interface InformeProvinciaDetalle {
  fecha:        string;          // YYYYMMDD del comprobante
  periodo:      string;
  cbte_tipo:    number;
  comprobante:  string;          // 00002-00000509
  cae:          string;
  cuit:         string;
  razon_social: string;
  collector:    string;
  provincia:    string;
  fuente:       string;          // arca | arca-cp | receptor | onboarding | domicilio-texto | domicilio-cp | ""
  motivo:       string;          // por qué quedó sin provincia (incluye el estado del padrón ARCA)
  /** CP-374: qué dijo el padrón de ARCA para este CUIT (trazabilidad). */
  arca_cp:        string;
  arca_localidad: string;
  arca_fecha:     string;        // ISO de la consulta al padrón ("" si nunca se consultó)
  arca_estado:    string;        // "" si ARCA dio provincia; sino "padrón ARCA sin consultar" / "padrón ARCA: <error>"
  neto:         number;
  iva:          number;
  total:        number;
}

export interface InformeProvincias {
  mes:          string;
  criterio:     InformeCriterio;
  empresa:      { cuit: number; razon_social: string };
  totales:      { neto: number; iva: number; total: number; facturas: number; clientes: number; filas: number };
  provincias:   InformeProvinciaRow[];
  sin_provincia: InformeProvinciaDetalle[];
  fuentes:      Record<string, number>;
  /** CP-374: estado del padrón de ARCA sobre los clientes (CUITs) del mes. */
  padron: {
    clientes: number; con_provincia: number; sin_consultar: number;
    sin_provincia: number; con_error: number;
    fecha_min: string; fecha_max: string; vigencia_dias: number;
  };
  notas_credito: number;
  detalle?:     InformeProvinciaDetalle[];
  generated_at: string;
}

export interface PadronRefreshResult {
  consultados: number;
  ok:        Array<{ cuit: string; razon_social: string; provincia: string; cod_postal: string; provincia_anterior: string }>;
  errores:   Array<{ cuit: string; error: string }>;
  pendientes: string[];
}

/** CP-374: consulta el padrón de ARCA para esos CUITs (≤ 25) y persiste el
 *  domicilio fiscal. Sincrónico y secuencial en el backend (~1-2 s por CUIT). */
export async function refreshPadron(cuits: string[]): Promise<PadronRefreshResult> {
  const res = await authFetch(`${API_BASE}/billing/informe-provincias/padron-refresh`, {
    method:  "POST",
    headers: { "Content-Type": "application/json", ...authHeader() },
    body:    JSON.stringify({ cuits }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body?.error ?? `POST /billing/informe-provincias/padron-refresh → ${res.status}`);
  }
  return res.json();
}

function informeProvinciasQs(mes: string, emitterCuit: number, criterio: InformeCriterio,
                             extra: Record<string, string> = {}): string {
  const p = new URLSearchParams({ mes, empresa: String(emitterCuit), criterio, ...extra });
  return `${API_BASE}/billing/informe-provincias?${p.toString()}`;
}

export async function getInformeProvincias(
  mes: string, emitterCuit: number, criterio: InformeCriterio = "comprobante", detalle = false,
): Promise<InformeProvincias> {
  const res = await authFetch(informeProvinciasQs(mes, emitterCuit, criterio, detalle ? { detalle: "1" } : {}),
                              { headers: { ...authHeader() } });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body?.error ?? `GET /billing/informe-provincias → ${res.status}`);
  }
  return res.json();
}

/** CSV del informe (resumen por provincia, o detalle por factura con `detalle`).
 *  Devuelve una URL de blob para disparar la descarga (como el ZIP de facturas). */
export async function downloadInformeProvinciasCsv(
  mes: string, emitterCuit: number, criterio: InformeCriterio, detalle: boolean,
): Promise<{ url: string; filename: string }> {
  const res = await authFetch(
    informeProvinciasQs(mes, emitterCuit, criterio, { format: "csv", ...(detalle ? { detalle: "1" } : {}) }),
    { headers: { ...authHeader() } },
  );
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body?.error ?? `GET /billing/informe-provincias (csv) → ${res.status}`);
  }
  const cd = res.headers.get("Content-Disposition") ?? "";
  const m = /filename="([^"]+)"/.exec(cd);
  const empresa = emitterCuit === 33718863169 ? "transferencias" : "b2bbnpl";
  const filename = m?.[1] ?? `iibb-provincias-${empresa}-${mes}${detalle ? "-detalle" : ""}.csv`;
  const blob = await res.blob();
  return { url: URL.createObjectURL(blob), filename };
}

export async function getBillingByProcessor(period: string): Promise<BillingByProcessor> {
  const res = await authFetch(
    `${API_BASE}/billing/by-processor?period=${encodeURIComponent(period)}`,
    { headers: { ...authHeader() } },
  );
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error ?? `getBillingByProcessor: ${res.status}`);
  return data as BillingByProcessor;
}
